import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';

import defaultImage from 'assets/images/missing_logo.svg';
import messageOutline from 'assets/images/messageOutline.svg';

import { TENDER_RESPONSE_URL } from 'global-constants';

import WithUser from 'shared/HOCs/WithUser';
import LoginRequired from 'shared/HOCs/LoginRequired';

import { formatDate } from 'shared/utils/dates';
import { downloadFile } from 'shared/utils/common';

import Image from 'shared/components/Image';
import Loader from 'shared/components/Loader';
import Button from 'shared/components/Button';
import CheckBox from 'shared/components/CheckBox';
import LoadMoreView from 'shared/components/LoadMoreView';
import ShareTenderModal from 'shared/components/ShareTenderModal';
import TenderResponseStatusForm from 'shared/components/TenderResponseStatusForm';

import { substituteUrl } from 'shared/utils/substituteUrl';

import NoTenders from '../NoTenders';

import {
  makeIntro,
  fetchTenders,
  deleteState,
  fetchResponses,
  fetchNextTenders,
  changeResponseStatus,
  downloadTenderResponseExport
} from '../ducks';

import { CLOSE_FILTERS, DISABLED_MAKE_INTRO_BUTTON_MESSAGE } from './constants';

import { getDownloadButtonLabel } from './utils';

import styles from './styles.module.scss';

const MobileDataItem = ({ title, children }) => (
  <div className={styles.dataItem}>
    <div className={styles.title}>{title}</div>
    <div className={styles.data}>{_.isNil(children) ? '-' : children}</div>
  </div>
);

class CloseTenders extends React.Component {
  state = {
    shareTenderModalTender: null,
    selectedTenderResponses: {},
    openTenders: [] // we use this to check which tenders have been expanded
  };

  componentDidMount() {
    const { orderingChoice: ordering } = this.props;

    this.props.fetchTenders({ ...CLOSE_FILTERS, ordering });
  }

  componentWillUnmount() {
    this.props.deleteState();
  }

  fetchResponsesIfNotPresent = ({ tenderId }) => {
    const { openTenders } = this.state;
    const { tenders, fetchResponses } = this.props;

    if (
      _.includes(openTenders, tenderId) &&
      _.isEmpty(_.find(tenders, tender => tender.id === tenderId).responses)
    ) {
      fetchResponses({ tenderId });
    }
  };

  toggleTenderOpen = ({ tenderId }) => {
    this.setState(
      prevState => ({
        openTenders: _.xor(prevState.openTenders, [tenderId])
      }),
      () => this.fetchResponsesIfNotPresent({ tenderId })
    );
  };

  // if we have an id in the state => the modal is open.
  toggleIsShareTenderModalOpen = ({ tender = null } = {}) =>
    this.setState({
      shareTenderModalTender: tender
    });

  redirectToTenderResponse = tenderResponseId => {
    this.props.history.push(
      substituteUrl(TENDER_RESPONSE_URL, { tenderResponseId })
    );
  };

  toggleTenderResponseCheckbox = ({ responseId, tenderId }) => {
    const selectedTenderResponsesForTender = _.xor(
      this.state.selectedTenderResponses[tenderId] || [],
      [responseId]
    );

    this.setState({
      selectedTenderResponses: {
        ...this.state.selectedTenderResponses,
        [tenderId]: selectedTenderResponsesForTender
      }
    });
  };

  downloadResponses = tenderId => {
    const { isFetchingExport, downloadTenderResponseExport } = this.props;
    const responseIds = _.get(
      this.state,
      `selectedTenderResponses.${tenderId}`,
      []
    );

    if (isFetchingExport) {
      return;
    }

    const responseHandlers = [
      responseData => downloadFile(responseData, 'export.csv')
    ];

    downloadTenderResponseExport({ tenderId, responseIds, responseHandlers });
  };

  makeIntro = responseId => {
    this.props.makeIntro(responseId);
  };

  render() {
    const {
      shareTenderModalTender,
      selectedTenderResponses,
      openTenders
    } = this.state;
    const {
      user,
      tenders,
      isFetching,
      isFetchingExport,
      nextUrl,
      fetchResponses,
      fetchNextTenders
    } = this.props;

    const shareTenderModalTenderId = _.get(shareTenderModalTender, 'id', null);

    return _.isEmpty(tenders) && !isFetching ? (
      <div className={styles.noTendersContainer}>
        <NoTenders type="closed" changeActiveTab={this.props.changeActiveTab} />
      </div>
    ) : (
      <>
        <div className={styles.header}>
          <div className={styles.content}>
            Please find below your closed projects. Once{' '}
            <span className={styles.bold}>all feedback has been delivered</span>{' '}
            to suppliers on a particular project, you’ll be able to open a
            conversation with those that you’ve shortlisted.
          </div>
          <div className={styles.hint}>
            <span className={styles.bold}>Hint:</span> Download multiple
            responses using the checkboxes below
          </div>
        </div>
        <div className={styles.tendersTable}>
          <LoadMoreView
            loadMore={() => fetchNextTenders({ nextUrl })}
            hasMore={!_.isNil(nextUrl)}
            isLoading={isFetching}
            loader={<Loader />}>
            {tenders.map((tender, index) => {
              const selectedTenderResponsesForTender =
                selectedTenderResponses[tender.id];
              const isSuperuser = user.is_superuser;
              const isTenderCollaborator = tender.is_collaborator;
              const isTenderOpen = _.includes(openTenders, tender.id);

              return (
                <React.Fragment key={`closed-tender-${tender.id}`}>
                  <div className={classnames(styles.tableHeader, 'remove-xs')}>
                    <div className={styles.title}>
                      <div className={styles.closingDate}>
                        Closed {formatDate(tender.closing_date)}
                      </div>
                    </div>
                    <div className={styles.companiesContacted}>
                      Companies Contacted
                    </div>
                    <div className={styles.responses}>Responses</div>
                    <div className={styles.totalVisits}>Total Visits</div>
                    <div className={styles.button} />
                  </div>

                  <div
                    key={`closed-tender-${index}`}
                    className={classnames(styles.tableRow, 'remove-xs')}>
                    <div className={styles.title}>{tender.title}</div>
                    <div className={styles.companiesContacted}>
                      {tender.companies_contacted || 0}
                    </div>
                    <div className={styles.responses}>
                      {tender.response_count}
                    </div>
                    <div className={styles.totalVisits}>
                      {tender.total_visits || 0}
                    </div>
                    <div className={styles.button}>
                      <div
                        className={styles.toggleResponses}
                        onClick={() =>
                          this.toggleTenderOpen({ tenderId: tender.id })
                        }>
                        View {isTenderOpen ? 'Less ' : 'More '}
                        <i className="material-icons">
                          {isTenderOpen
                            ? 'keyboard_arrow_up'
                            : 'keyboard_arrow_down'}
                        </i>
                      </div>
                    </div>
                  </div>

                  <div
                    className={classnames(styles.mobileTitle, 'remove-sm-min')}
                    onClick={() =>
                      this.toggleTenderOpen({ tenderId: tender.id })
                    }>
                    <div className={styles.itemTitle}>
                      Title
                      <i className="material-icons">
                        {isTenderOpen
                          ? 'keyboard_arrow_up'
                          : 'keyboard_arrow_down'}
                      </i>
                    </div>
                    {tender.title}
                    <MobileDataItem title="Companies Contacted">
                      {tender.companies_contacted}
                    </MobileDataItem>
                    <MobileDataItem title="Responses">
                      {tender.response_count}
                    </MobileDataItem>
                    <MobileDataItem title="Total Visits">
                      {tender.total_visits}
                    </MobileDataItem>
                  </div>

                  {isTenderOpen && (
                    <>
                      <div className={styles.actionsRow}>
                        <div className={styles.left}>
                          <div className={styles.text}>
                            {_.get(
                              selectedTenderResponsesForTender,
                              'length',
                              0
                            )}{' '}
                            Selected
                          </div>
                          <div
                            className={classnames(styles.download, {
                              [styles.disabled]: isFetchingExport
                            })}
                            onClick={() => this.downloadResponses(tender.id)}>
                            {getDownloadButtonLabel(
                              selectedTenderResponsesForTender
                            )}
                          </div>
                        </div>

                        <div className={styles.right}>
                          {(isTenderCollaborator || isSuperuser) && (
                            <div
                              className={styles.share}
                              onClick={() =>
                                this.toggleIsShareTenderModalOpen({
                                  tender
                                })
                              }>
                              Share Project
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.tenderResponsesTable}>
                        <LoadMoreView
                          hasMore={!_.isNil(tender.responsesNextUrl)}
                          isLoading={_.get(tender, 'isFetchingResponses', true)}
                          loadMore={() =>
                            fetchResponses({
                              tenderId: tender.id,
                              nextUrl: tender.responsesNextUrl
                            })
                          }
                          loader={<Loader />}>
                          {_.map(tender.responses, response => {
                            const {
                              vendor,
                              service_provider: serviceProvider
                            } = response;

                            const company = vendor || serviceProvider;

                            const isMakeIntroBtnDisabled = !(
                              _.get(response.status, 'label') ===
                                'Shortlisted' &&
                              _.isEmpty(
                                _.filter(
                                  tender.responses,
                                  item => !_.isNil(company) && !item.status
                                )
                              )
                            );

                            const isTenderResponseStatusFormDisabled =
                              _.isNil(company) ||
                              _.get(response, 'created_by.slug') ===
                                user.slug ||
                              !isTenderCollaborator;
                            const hasNotes = response.note_count > 0;

                            return (
                              <div
                                className={styles.tenderResponsesRow}
                                key={`tender-response-${response.id}`}>
                                <CheckBox
                                  className={styles.checkBox}
                                  name={`tender-response-checkbox-${
                                    response.id
                                  }`}
                                  key={response.id}
                                  onChange={() =>
                                    this.toggleTenderResponseCheckbox({
                                      responseId: response.id,
                                      tenderId: tender.id
                                    })
                                  }
                                  checked={(
                                    selectedTenderResponsesForTender || []
                                  ).includes(response.id)}
                                />
                                <Image
                                  width={60}
                                  height={60}
                                  className={styles.logo}
                                  src={_.get(company, 'logo')}
                                  thumbnails={_.get(company, 'logo_thumbnails')}
                                  defaultImage={defaultImage}
                                />
                                <div className={styles.company}>
                                  {_.get(company, 'name', 'Unknown Company')}
                                </div>
                                <TenderResponseStatusForm
                                  id={response.id}
                                  className={styles.status}
                                  isDisabled={
                                    isTenderResponseStatusFormDisabled
                                  }
                                  defaultValue={response.status}
                                  extraActions={[
                                    selectedOption =>
                                      changeResponseStatus({
                                        tenderId: tender.id,
                                        responseId: response.id,
                                        status: selectedOption
                                      })
                                  ]}
                                />
                                <div
                                  className={classnames(styles.notes, {
                                    [styles.withIcon]: hasNotes
                                  })}>
                                  {hasNotes && `${response.note_count} notes`}
                                </div>
                                <span
                                  className={styles.viewBtn}
                                  onClick={() =>
                                    this.redirectToTenderResponse(response.id)
                                  }>
                                  View{' '}
                                  <span className="remove-md">Response</span> >
                                </span>
                                {response.introduced ? (
                                  'Introduced'
                                ) : (
                                  <Button
                                    isIcon={false}
                                    color="brand-green"
                                    title={
                                      _.isNil(company)
                                        ? DISABLED_MAKE_INTRO_BUTTON_MESSAGE
                                        : ''
                                    }
                                    onClick={() => this.makeIntro(response.id)}
                                    className={styles.makeIntroBtn}
                                    disabled={
                                      isMakeIntroBtnDisabled ||
                                      _.isNil(company) ||
                                      !isTenderCollaborator
                                    }>
                                    Make Intro
                                    <img
                                      src={messageOutline}
                                      className="remove-md"
                                      alt="message-icon-outline"
                                    />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </LoadMoreView>
                      </div>
                      <Button
                        className={classnames(
                          styles.mobileDownloadBtn,
                          'remove-sm-min'
                        )}
                        color="primary"
                        isLoading={isFetchingExport}
                        onClick={() => this.downloadResponses(tender.id)}>
                        {getDownloadButtonLabel(
                          selectedTenderResponsesForTender
                        )}
                      </Button>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </LoadMoreView>
          {!_.isNil(shareTenderModalTenderId) && (
            <ShareTenderModal
              tenderId={shareTenderModalTenderId}
              closeModal={this.toggleIsShareTenderModalOpen}
              canSubmit={shareTenderModalTender.is_collaborator}
            />
          )}
        </div>
      </>
    );
  }
}
const mapStateToProps = state => {
  const {
    nextUrl,
    tenders,
    isFetching,
    isFetchingExport
  } = state.pages.tender.create;
  const orderingChoice = _.find(
    state.constants.constants.users.TENDER_ORDERING_CHOICES,
    ['label', 'Ending latest']
  );

  return {
    nextUrl,
    tenders,
    isFetching,
    isFetchingExport,
    orderingChoice
  };
};

const mapDispatchToProps = {
  fetchNextTenders,
  makeIntro,
  fetchTenders,
  deleteState,
  fetchResponses,
  downloadTenderResponseExport
};

export default LoginRequired(
  WithUser(
    withRouter(
      connect(
        mapStateToProps,
        mapDispatchToProps
      )(CloseTenders)
    )
  )
);
